import React from "react";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";
import { ArrowRight } from "lucide-react";

const HERO_ILLUSTRATION =
  "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/yo5juwnt_ChatGPT%20Image%20May%2022%2C%202026%20at%2001_50_41%20PM.png";

export const Hero = () => {
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative bg-bone overflow-hidden"
    >
      {/* FULL-BLEED illustration — fills the entire hero, never cropped */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={HERO_ILLUSTRATION}
          alt=""
          aria-hidden="true"
          data-testid="hero-illustration"
          className="absolute inset-0 w-full h-full object-cover object-right"
        />
        {/* soft warm gradient so the text on the left remains legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/85 to-bone/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-bone/30 via-transparent to-bone/40" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-24 md:pb-36 min-h-[92vh] flex items-center">
        <motion.div
          variants={stagger(0.14)}
          initial="hidden"
          animate="visible"
          className="max-w-2xl"
        >
          <motion.p
            variants={fadeUp}
            className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-bronze mb-8"
            data-testid="hero-eyebrow"
          >
            ✦ &nbsp; Harmonia Portal &nbsp; ✦
          </motion.p>

          <motion.h1
            variants={fadeUp}
            data-testid="hero-title"
            className="font-serif text-[16vw] sm:text-[12vw] lg:text-[8.5rem] xl:text-[9.5rem] leading-[0.92] text-charcoal font-light tracking-tight"
          >
            <span className="block">Hadil</span>
            <span className="block italic text-bronze -mt-2 md:-mt-3">El-Baba</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            data-testid="hero-subtitle-lead"
            className="mt-12 md:mt-14 max-w-xl font-serif italic text-2xl md:text-[1.85rem] lg:text-[2rem] text-bronze leading-[1.2]"
          >
            Designing the invisible conditions that help people, teams,
            and communities thrive.
          </motion.p>

          <motion.p
            variants={fadeUp}
            data-testid="hero-subtitle"
            className="mt-7 text-xs md:text-sm uppercase tracking-[0.22em] text-ink"
          >
            Architect <span className="text-bronze">•</span> Facilitator{" "}
            <span className="text-bronze">•</span> Writer{" "}
            <span className="text-bronze">•</span> Experience Curator{" "}
            <span className="text-bronze">•</span> Systems Thinker
          </motion.p>

          <motion.p
            variants={fadeUp}
            data-testid="hero-founder"
            className="mt-4 text-[11px] md:text-xs uppercase tracking-[0.3em] text-bronze"
          >
            ✦ &nbsp; Founder of{" "}
            <span className="font-serif italic normal-case tracking-normal text-base md:text-lg text-bronze">
              Harmonia
            </span>
          </motion.p>

          <motion.p
            variants={fadeUp}
            data-testid="hero-description"
            className="mt-8 max-w-xl text-lg md:text-xl text-ink/95 font-light leading-relaxed text-pretty"
          >
            Through{" "}
            <span className="italic font-serif text-bronze text-[1.4em]">
              systems thinking
            </span>
            , dialogue, and experiential design, I help transform insight
            into connection, belonging, and collective action. Drawing
            from my background in architecture, I apply design principles
            beyond buildings, supporting individuals in becoming architects
            of their inner world, teams in aligning around shared vision
            and purpose, and communities in cultivating the conditions for
            meaningful participation and collective flourishing.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-work"
              onClick={scrollTo("frameworks")}
              className="btn-pill group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-charcoal text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-forest"
            >
              Explore my work
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-journey"
              onClick={scrollTo("offerings")}
              className="btn-pill group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-charcoal/40 text-charcoal text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal hover:text-bone"
            >
              Begin your journey
            </button>
            <button
              data-testid="hero-cta-connect"
              onClick={scrollTo("connect")}
              className="btn-pill text-[11px] uppercase tracking-[0.22em] text-bronze link-underline"
            >
              Let&apos;s connect
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* bottom rule */}
      <div className="relative border-t border-stone/40 bg-bone/80 backdrop-blur-sm">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone italic font-serif">
            Scroll, slowly
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone">
            ✦ &nbsp; observe , reflect , connect , design , evolve
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
